import {
  Autocomplete as MUIAutocomplete,
  TextField,
  FormControl,
  FormHelperText,
  Typography,
  useTheme
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { Controller, Control } from 'react-hook-form';
import { ReactNode } from 'react';

interface Option {
  label: string | ReactNode;
  value: string | number | boolean;
  disabled?: boolean;
}

interface Props {
  name: string;
  control: Control<any>;
  label: string;
  options: Option[];
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
  onChange?: any;
}

export default function Autocomplete({
  name,
  control,
  label,
  options,
  placeholder,
  disabled,
  required,
  fullWidth = true,
  size,
  sx,
  onChange
}: Props) {
  const theme = useTheme();

  return (
    <FormControl fullWidth={fullWidth}>
      <Controller
        name={name}
        control={control}
        rules={{
          required: required ? `${label} is required` : false
        }}
        render={({ field, fieldState: { error } }) => {
          const currentValue = options.find((opt) => opt.value === field.value) || null;

          return (
            <>
              <MUIAutocomplete
                options={options}
                size={size}
                getOptionLabel={(option) => (typeof option.label === 'string' ? option.label : String(option.value))}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                onChange={(_, newValue) => {
                  onChange ? onChange(newValue) : field.onChange(newValue?.value ?? null);
                }}
                value={currentValue}
                disabled={disabled}
                sx={{
                  ...sx
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size={size}
                    label={
                      <span style={{ display: 'flex', gap: 2 }}>
                        {label}
                        {required && (
                          <Typography component='span' color='#DA0808'>
                            *
                          </Typography>
                        )}
                      </span>
                    }
                    InputLabelProps={{
                      ...params.InputLabelProps
                    }}
                    sx={{
                      '.MuiOutlinedInput-notchedOutline': {
                        borderColor: '#ececec'
                      }
                    }}
                    placeholder={placeholder}
                    error={!!error}
                  />
                )}
              />
              {error && <FormHelperText sx={{ color: theme.palette.error.main }}>{error.message}</FormHelperText>}
            </>
          );
        }}
      />
    </FormControl>
  );
}
